import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Button, Container } from '../components/site/ui';
import PulseSection from '../components/PulseSection';
import {
  IconArrowRight,
  IconBell,
  IconCases,
  IconCheck,
  IconLock,
  IconMail,
  IconPackage
} from '../components/icons';

const steps = [
  { title: 'You report the issue', description: 'Choose the problem, add tracking details, upload evidence.' },
  { title: 'We prepare the evidence pack', description: 'Everything the retailer needs, bundled clearly.' },
  { title: 'The retailer reviews it', description: 'They receive a secure, read-only link.' },
  { title: 'You get a response', description: 'Decision sent by email — or SMS if you choose updates.' }
];

const issueTypes = [
  { title: 'Delivered but not received', description: "Courier says delivered, but you didn't receive it." },
  { title: 'Delayed / possibly lost', description: "Your parcel should have arrived but hasn't." },
  { title: 'Damaged on arrival', description: 'The parcel arrived but contents are damaged.' },
  { title: 'Missing item(s)', description: 'Items from your order are not in the parcel.' },
  { title: 'Left in unsafe place', description: 'The courier left your parcel somewhere insecure.' }
];

const assurances = [
  { icon: IconPackage, label: 'Free for customers' },
  { icon: IconLock, label: 'No account required' },
  { icon: IconMail, label: 'Email updates included' }
];

const CustomersPage = () => {
  const [smsPrice, setSmsPrice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/merchants/pricing')
      .then(({ data }) => {
        if (!cancelled && typeof data?.smsPrice === 'number') setSmsPrice(data.smsPrice);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="bg-brand-900">
        <Container width="full" className="py-12 sm:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <div>
              <p className="text-[11px] font-700 uppercase tracking-[0.14em] text-gold-400">
                For customers
              </p>
              <h1 className="mt-3 font-display text-[34px] font-800 leading-[1.08] tracking-tight text-white sm:text-[44px]">
                Submit once —
                <br />
                <span className="text-accent-400">properly.</span>
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/80">
                ParcelBuddy turns a delivery complaint into a structured case with the evidence
                attached, and puts it in front of the retailer who can actually resolve it.
              </p>

              <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                {assurances.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-2 text-sm font-medium text-white/85">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-500 text-white">
                      <Icon size={14} />
                    </span>
                    {label}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button as={Link} to="/report" variant="accent" size="lg">
                  Report a delivery issue
                  <IconArrowRight size={18} />
                </Button>
                <p className="text-sm text-white/60">Takes about 2–3 minutes.</p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-2xl bg-white/10 p-6 ring-1 ring-inset ring-white/15">
                <p className="font-display text-3xl font-800 tracking-tight text-white">Free</p>
                <p className="mt-2 text-[15px] font-600 text-white">Free for customers</p>
                <p className="mt-1 text-sm leading-relaxed text-white/70">
                  Submit once — properly. No fees, no account required.
                </p>
              </div>

              <div className="rounded-2xl bg-gold-500 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-900/15 text-brand-900">
                  <IconBell size={19} />
                </span>
                <p className="mt-4 text-[15px] font-700 text-brand-900">
                  Optional SMS updates
                  {smsPrice != null && ` — £${smsPrice.toFixed(2)} per case`}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-brand-900/75">
                  Get a text when the retailer reviews or resolves your case. Email updates are
                  always free.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* How it works */}
      <section className="bg-white py-12 sm:py-14">
        <Container width="full">
          <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-[28px]">
            How it works
          </h2>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-ink-600">
            Four steps, and the retailer gets everything they need the first time.
          </p>

          <ol className="mt-8 grid gap-x-4 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, i) => (
              <li key={step.title}>
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-full font-display text-base font-800 ${
                    i % 2 === 1 ? 'bg-gold-500 text-brand-900' : 'bg-brand-700 text-white'
                  }`}
                >
                  {i + 1}
                </span>
                <p className="mt-3.5 font-display text-[15px] font-700 text-ink-900">{step.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{step.description}</p>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      {/* Issue types */}
      <section className="bg-brand-50/60 py-12 sm:py-14">
        <Container width="full">
          <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-[28px]">
            Issue types we handle
          </h2>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-ink-600">
            If your parcel went wrong in one of these ways, we can structure it.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {issueTypes.map((issue) => (
              <div
                key={issue.title}
                className="flex items-start gap-3.5 rounded-xl border border-ink-200 bg-white p-4 shadow-card"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
                  <IconCases size={19} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-[15px] font-700 text-ink-900">{issue.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-ink-600">{issue.description}</p>
                </div>
              </div>
            ))}

            <div className="flex flex-col justify-between rounded-xl bg-brand-900 p-5">
              <div>
                <p className="font-display text-[15px] font-700 leading-snug text-white">
                  Ready to report yours?
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-brand-100/70">
                  It&apos;s free, and no account is required.
                </p>
              </div>
              <Button as={Link} to="/report" variant="accent" className="mt-5 w-full">
                Report a delivery issue
                <IconArrowRight size={17} />
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="bg-white py-12 sm:py-14">
        <Container width="full">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-brand-800 px-6 py-8 sm:px-8 lg:flex-row lg:items-center">
            <div className="max-w-xl">
              <h2 className="font-display text-2xl font-700 tracking-tight text-white">
                Had a delivery go wrong?
              </h2>
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                {['Free to use', 'No account needed', 'Takes 2 minutes'].map((label) => (
                  <li key={label} className="flex items-center gap-1.5 text-sm text-white/80">
                    <IconCheck size={14} className="text-gold-400" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
            <Button as={Link} to="/report" variant="accent" size="lg" className="shrink-0">
              Report a delivery issue
              <IconArrowRight size={18} />
            </Button>
          </div>
        </Container>
      </section>
      {/* The same Pulse panel the home page shows, with the figures that
          speak to this audience. */}
      <PulseSection
        panels={['customer']}
        title="What customers are reporting"
        description="Live figures from the delivery issues people are reporting through ParcelBuddy."
      />

    </>
  );
};

export default CustomersPage;
